import React, { useState, useEffect } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Skeleton
} from '@mui/material';
import {
  PendingActions,
  Cancel,
  People,
  TrendingUp
} from '@mui/icons-material';
import { apiService } from '../../config/api';

interface StatsData {
  pendingApplications: number;
  approvedResearchers: number;
  rejectedApplications: number;
  totalApplications: number;
}

interface MasterStatsProps {
  loading: boolean;
}

const MasterStats: React.FC<MasterStatsProps> = ({ loading: parentLoading }) => {
  const [stats, setStats] = useState<StatsData>({
    pendingApplications: 0,
    approvedResearchers: 0,
    rejectedApplications: 0,
    totalApplications: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      
      // Load all stats in parallel
      const [pendingRes, approvedRes, rejectedRes, researchersRes] = await Promise.all([
        apiService.get('/api/admin/researcher-applications?status=pending'),
        apiService.get('/api/admin/researcher-applications?status=approved'),
        apiService.get('/api/admin/researcher-applications?status=rejected'),
        apiService.get('/api/admin/researchers?status=all')
      ]);


      setStats({
        pendingApplications: pendingRes.applications?.length || 0,
        approvedResearchers: researchersRes.researchers?.length || 0,
        rejectedApplications: rejectedRes.applications?.length || 0,
        totalApplications: (pendingRes.applications?.length || 0) + 
                          (approvedRes.applications?.length || 0) + 
                          (rejectedRes.applications?.length || 0)
      });
      
    } catch (error) {
      // Error loading stats, will show loading skeleton
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ 
    title, 
    value, 
    icon, 
    color = 'primary' 
  }: { 
    title: string; 
    value: number; 
    icon: React.ReactNode; 
    color?: 'primary' | 'warning' | 'success' | 'error' | 'info';
  }) => (
    <Card elevation={2} sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box sx={{ 
            p: 1, 
            borderRadius: 1, 
            bgcolor: `${color}.light`,
            color: `${color}.contrastText`,
            mr: 2
          }}>
            {icon}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" component="div" color="text.secondary">
              {title}
            </Typography>
            {loading || parentLoading ? (
              <Skeleton variant="text" width={30} height={30} />
            ) : (
              <Typography variant="h3" component="div" color={`${color}.main`} fontWeight="bold">
                {value}
              </Typography>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          title="Pending Applications"
          value={stats.pendingApplications}
          icon={<PendingActions />}
          color="warning"
        />
      </Grid>
      
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          title="Active Researchers"
          value={stats.approvedResearchers}
          icon={<People />}
          color="success"
        />
      </Grid>
      
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          title="Total Applications"
          value={stats.totalApplications}
          icon={<TrendingUp />}
          color="info"
        />
      </Grid>
      
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          title="Rejected Applications"
          value={stats.rejectedApplications}
          icon={<Cancel />}
          color="error"
        />
      </Grid>
    </Grid>
  );
};

export default MasterStats;
